/**
 * The family's day, counted together: how many are in, how long everyone has
 * kept it up, and who is leading.
 *
 * Its own module, and pure, like feed.ts: the screen reaches Firebase through
 * ../data, and these are the rules that decide what the row of faces says.
 *
 * Individual streaks were all the row had, in ten-pixel type under each name,
 * and a streak on its own is a lonely number. These give the family something
 * to keep up as one.
 */
import { NOT_FAMILY_EMAILS } from '../../shared/constants';

export interface Member {
  id: string;
  /** Their current run, as streakOf gives it for the day being counted. */
  streak: number;
  /** Whether they have answered the prompt being counted. */
  posted: boolean;
}

/** Counted with the family, which leaves out test accounts. */
export function isFamily(user: { email?: string } | undefined): boolean {
  const email = user?.email?.trim().toLowerCase();
  return !email || !NOT_FAMILY_EMAILS.includes(email);
}

export interface Standing {
  /** How many have answered the prompt being counted. */
  posted: number;
  size: number;
  /**
   * Days in a row that everybody has posted.
   *
   * No new record is needed for it. Each person's streak is the run of days
   * they posted, and every run ends at the day being counted or the one before
   * -- so a day belongs to all of them exactly when it falls inside the
   * shortest. The shortest streak is the family's.
   */
  streak: number;
  /** The longest current run, and whoever holds it. Nobody below two. */
  crowned: ReadonlySet<string>;
}

/*
 * A crown for a single day would go to whoever posted first after any break,
 * and change hands for nothing. Two days is the least that is a run.
 */
export const CROWN_FROM = 2;

export function standing(members: Member[]): Standing {
  const streaks = members.map((m) => m.streak);
  const longest = streaks.length ? Math.max(...streaks) : 0;

  return {
    posted: members.filter((m) => m.posted).length,
    size: members.length,
    streak: streaks.length ? Math.min(...streaks) : 0,
    crowned: new Set(
      longest >= CROWN_FROM ? members.filter((m) => m.streak === longest).map((m) => m.id) : [],
    ),
  };
}

/**
 * Their run is alive but today is not in it yet.
 *
 * A streak holds until the next prompt fires, so anybody with one who has not
 * answered this prompt loses it at the next. Nobody with no run has anything
 * on the line.
 */
export function atRisk(member: Member): boolean {
  return !member.posted && member.streak > 0;
}

/** Who answered the prompt being counted first, among the family. */
export function firstIn(
  posts: { data: { authorUid: string; createdAt: unknown } }[],
  counts: (uid: string) => boolean,
): string | null {
  let first: { uid: string; at: number } | null = null;
  for (const post of posts) {
    if (!counts(post.data.authorUid)) continue;
    const at = (post.data.createdAt as { toMillis?: () => number } | null)?.toMillis?.();
    // One still being written has no time yet, and cannot have been first.
    if (at === undefined) continue;
    if (!first || at < first.at) first = { uid: post.data.authorUid, at };
  }
  return first?.uid ?? null;
}
